"use client";

import { useState } from "react";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";

interface FaqItem {
  question: string;
  answer: string;
}

interface SecurityHeadersFaqProps {
  faqs: FaqItem[];
}

export function SecurityHeadersFaq({ faqs }: SecurityHeadersFaqProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section
      className="space-y-8"
      aria-label="Frequently asked questions about security headers"
    >
      <div className="space-y-2 text-center">
        <div className="inline-flex items-center px-3 py-1 rounded-full border border-[#f97316] mb-4">
          <span className="text-xs font-medium text-[#f97316] dark:text-[#f97316] uppercase tracking-wider">
            FAQ
          </span>
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
          Everything you need to
          <span className="block text-[#f97316] pb-1">secure headers</span>
        </h2>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          Get answers to the most common questions about HTTPS enforcement, HSTS,
          CSP, and anti-clickjacking policies.
        </p>
      </div>

      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <div className="space-y-3">
          {faqs.map((item, index) => {
            const isOpen = openIndex === index;

            return (
              <div
                key={item.question}
                className={cn(
                  "rounded-2xl border bg-card shadow-sm transition-all duration-200",
                  isOpen
                    ? "border-white/20"
                    : "border-border/60 hover:border-white/30"
                )}
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="flex w-full items-start justify-between gap-4 px-6 py-5 text-left"
                  aria-expanded={isOpen}
                >
                  <span className="flex-1 text-sm sm:text-base font-medium text-foreground leading-relaxed">
                    {item.question}
                  </span>
                  <ChevronDown
                    className={cn(
                      "mt-0.5 h-4 w-4 text-muted-foreground transition-transform duration-200 flex-shrink-0",
                      isOpen ? "rotate-180" : ""
                    )}
                    aria-hidden
                  />
                </button>
                <div
                  className={cn(
                    "overflow-hidden transition-all duration-200 ease-out",
                    isOpen ? "max-h-[220px] opacity-100" : "max-h-0 opacity-0"
                  )}
                >
                  <div className="px-6 pb-5 text-sm text-muted-foreground leading-relaxed">
                    {item.answer}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
